import { useCallback, useMemo, useRef } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import type { BizyUiBlockAction, BizyUiBlockRecipientRef, GifAttachment } from '@bonusly/bizy-kit';
import { useDispatch } from '@bonusly/events';
import { Recipient } from '@bonusly/recipient-entities';

import { useRecordGiveboxEditIntentMutation } from '../../../queries/bizy';
import { AttachmentCategory, SocialAttachment } from '../../action-input/lib/entities/attachment';
import { GiveBoxEvent } from '../../recognition/give-box/events';
import type { BizyCommentComposerHandle } from '../store/use-bizy-active-composers-store';
import type { BizyContextualInsertRequest } from '../store/use-bizy-contextual-insert-store';
import type { BizyPanelTrigger } from '../store/use-bizy-side-panel-store';

/**
 * The dispatcher is the single place that knows how each action `type`
 * maps to a side-effect (composer insert, give-box open, navigate, etc.).
 * UI block renderers never call surface APIs directly — they hand a typed
 * action to the dispatcher provided by the host chat view.
 *
 * Adding a new action type:
 *   1. Add the schema variant in `packages/bizy-kit/src/types/ui-blocks.ts`.
 *   2. Add a branch in {@link buildBizyUiBlockActionDispatcher} below.
 *   3. (Backend) have a tool declare the action on its payload.
 *
 * That's it — no renderer changes, no chat-view plumbing.
 */
export type BizyUiBlockActionDispatcher = (action: BizyUiBlockAction) => void;

interface BuildDispatcherDeps {
	/** Current contextual `/` invocation, if any. Used for the panel-close lifecycle and analytics. */
	pendingContextualInsert: BizyContextualInsertRequest | null;
	/** Clears the pending contextual session after an action completes. */
	clearPendingContextualInsert: () => void;
	/** Closes the side panel; called after actions that hand off to another surface. */
	closePanel: (trigger?: BizyPanelTrigger) => void;
	/** React Router navigator for `view_post`. */
	navigate: NavigateFunction;
	/** Active Bizy conversation id (from the chat connector). Used to attribute the edit-in-givebox intent. */
	currentConversationId?: string;
	/** Event bus dispatch for `open_in_givebox`. */
	dispatch: ReturnType<typeof useDispatch>;
	/** Analytics track function. */
	track: ReturnType<typeof useAnalytics>['track'];
	/**
	 * Registry lookup for mounted comment composers, keyed by bonus id.
	 * Returned handle has `insertDraft` / `sendNow` /
	 * `scrollIntoViewAndFocus`. Returns undefined when no composer is
	 * mounted for that bonus (parent post unmounted, user navigated
	 * away). For the unmounted case the dispatcher uses
	 * `queuePendingCommentInsert` + `navigate('/bonuses/{id}')` so the
	 * draft lands when the bonus modal mounts and registers its
	 * composer.
	 */
	getCommentComposer: (bonusId: string) => BizyCommentComposerHandle | undefined;
	/**
	 * Stash a comment draft for a bonus whose composer isn't mounted
	 * yet. Pairs with a `navigate('/bonuses/{bonusId}')` call from the
	 * dispatcher: the bonus modal mounts the comments tree, the
	 * `CommentComposer` calls `registerCommentComposer`, and the store
	 * drains the queued draft into the freshly-mounted handle on the
	 * same tick. Without this, Insert-draft for a bonus the user has
	 * navigated away from would silently miss (or — historically —
	 * land in the give-box, which is the wrong destination because
	 * comments don't carry a recipient).
	 */
	queuePendingCommentInsert: (bonusId: string, text: string) => void;
	/**
	 * Posts a user message into the live Bizy conversation (just like
	 * the user typing into the input). The `extraContext` becomes
	 * `bizy_*` keys on the request — the dispatcher uses this for
	 * `send_now` on a recognition draft to fire a real chat turn that
	 * Bizy answers via its `give_recognition` tool, instead of opening
	 * the give-box modal. Keeps the conversation legible and routes
	 * the bonus through Bizy's audited tool-call path.
	 */
	sendChatMessage: (content: string, extraContext?: Record<string, string>, displayOptions?: { attachmentPreviewUrl?: string }) => void;
	/**
	 * Enters a persistent contextual drafting session (the same store the
	 * `/` comment composer uses). Used by `request_draft` (Pollinator) so the
	 * comment surface + parent post stick to EVERY follow-up turn — not just
	 * the first — and the panel auto-sends the friendly starter. Without this,
	 * a follow-up like "make it 10 points" loses the comment surface and Bizy
	 * reverts to drafting a brand-new give-box recognition.
	 */
	setPendingContext: (context: BizyContextualInsertRequest) => void;
	/**
	 * Records that the user opened a Bizy recognition draft in the give box
	 * ("Edit in give box") as a PENDING edit-in-givebox intent. Fire-and-forget;
	 * the give-box hand-off is unaffected. Owned by the Bizy domain (a backend
	 * reconciler later ties it to the posted bonus + computes the diff).
	 */
	recordGiveboxEditIntent: (payload: { conversationId: string; recipientIds: string[]; amount?: number; hashtags?: string[]; message: string }) => void;
}

/**
 * Pure dispatcher factory — exported for tests and for callers that
 * want to construct a dispatcher outside of React (e.g. from a
 * headless handler). React consumers should use
 * {@link useBuildBizyUiBlockActionDispatcher} below.
 */
export function buildBizyUiBlockActionDispatcher(deps: BuildDispatcherDeps): BizyUiBlockActionDispatcher {
	const {
		clearPendingContextualInsert,
		closePanel,
		navigate,
		currentConversationId,
		dispatch,
		track,
		getCommentComposer,
		queuePendingCommentInsert,
		sendChatMessage,
		setPendingContext,
		recordGiveboxEditIntent,
	} = deps;

	// Read the contextual session LIVE on each call, never via a captured
	// const. The React hook supplies `deps.pendingContextualInsert` as a getter
	// so the memoized dispatcher (rebuilt only when other deps change) always
	// sees the current session; destructuring it would freeze a stale value
	// (typically the `null` from when the dispatcher was last built), which
	// would silently break panel-close, analytics, and the autodraft-session
	// teardown below.
	const currentSession = (): BizyContextualInsertRequest | null => deps.pendingContextualInsert;

	// Only close the side panel after a completion action when `/` was
	// what opened it. If the user already had Bizy open (chatting in the
	// drawer) and triggered `/` mid-conversation, leave the panel open so
	// they can keep going — closing it on Insert / Send now / Give now
	// would feel like Bizy is taking over their chat. When there's no
	// active contextual session at all (e.g. a draft card from a regular
	// chat reply), default to closing on completion to match historical
	// behavior of these handoff actions.
	const shouldClosePanelOnCompletion = () => {
		const session = currentSession();
		if (!session) return true;
		return !session.panelWasAlreadyOpen;
	};
	const closePanelIfSlashOpened = () => {
		if (shouldClosePanelOnCompletion()) closePanel('ui_block_action');
	};

	// A Pollinator request_draft session (autodraft) persists across the drafting
	// iteration ("make it 10 points") so the comment surface + parent post stick
	// — but it MUST end once the user commits or hands the draft to the composer,
	// or later turns in the same already-open conversation keep carrying the stale
	// comment surface + parentBonusId and Bizy keeps drafting on the old post. The
	// `/` composer flow keeps its own lifecycle (cleared on panel close), so we
	// only end the autodraft (Pollinator) session here.
	const endAutodraftSession = () => {
		if (currentSession()?.surfaceData?.autodraft) clearPendingContextualInsert();
	};

	const handOffToGiveBox = (
		text: string,
		options: { recipientRef?: BizyUiBlockRecipientRef; amount?: number; hashtags?: string[]; autoSend?: boolean; attachment?: SocialAttachment } = {}
	) => {
		const { recipientRef, amount, hashtags, autoSend, attachment } = options;
		// The give-box modal expects a `Recipient` object, not a username
		// string — without one the @ pill mounts blank and the user
		// can't submit. Backend tools that know the resolved user pass
		// `recipientRef`; we instantiate a Recipient from it here.
		const recipient = recipientRef ? buildRecipient(recipientRef) : undefined;
		dispatch(GiveBoxEvent.Open, {
			aiDraft: text,
			...(recipient ? { recipient } : {}),
			...(amount != null ? { amount } : {}),
			...(hashtags && hashtags.length > 0 ? { hashtags } : {}),
			// Carry the gif the user picked in chat into the give-box so
			// "Edit in Givebox" opens with it already attached.
			...(attachment ? { attachment } : {}),
			// `autoSend` triggers the give-box to submit the form right
			// after the draft inserts — this is how the "Give now" action
			// on a recognition_draft_card reaches one-click send while
			// still going through the normal validation path.
			...(autoSend ? { autoSend: true } : {}),
		});
		closePanelIfSlashOpened();
	};

	const trackContextual = (eventName: string, actionLabel: string) => {
		const surface = currentSession()?.surface;
		if (!surface) return;
		track(eventName, { surface, path: window.location.pathname, action: actionLabel });
	};

	return (action) => {
		switch (action.type) {
			case 'insert_into_draft': {
				// Comment surface: route to the mounted composer for the
				// bonus on the action. The registry stays alive as long
				// as the composer is mounted — clicks are idempotent and
				// don't depend on `pendingContextualInsert` (which gets
				// cleared on panel close, so the second click would
				// otherwise miss).
				if (action.parentBonusId) {
					// Prefix `+N ` when the action carries an add-on
					// amount so the composer's BonusReasonParser derives
					// the right amount from `data().amount`. Without this,
					// a draft card showing "+5" inserts only the plain
					// text and the user submits a zero-point comment
					// (silent regression — UI pill doesn't match what
					// gets posted).
					const formatted = formatCommentDraftText(action.text, action.amount);
					const composer = getCommentComposer(action.parentBonusId);
					if (composer) {
						trackContextual(AnalyticsEvent.Bizy.ContextualInserted, 'insert_into_draft');
						composer.insertDraft(formatted);
						endAutodraftSession();
						closePanelIfSlashOpened();
						return;
					}
					// Composer not mounted — user navigated away from the
					// parent post mid-`/`-session, or clicked Insert from
					// a chat panel that's no longer co-located with the
					// post. Queue the draft and navigate to the bonus
					// modal: the comment composer mounts on
					// `/bonuses/{id}`, calls `registerCommentComposer`,
					// and the store drains the queued draft into the
					// fresh handle on the same tick.
					//
					// We deliberately do NOT fall through to the give-box
					// here. A comment doesn't have a recipient — handing
					// the text to the give-box would prompt the user for
					// a recipient (which makes no sense for a pile-on)
					// and silently shift the post from "comment on
					// {parentBonus}" to "new top-level recognition".
					// HABITS-1294 follow-up: the wrong destination was
					// worse than no destination.
					trackContextual(AnalyticsEvent.Bizy.ContextualInserted, 'insert_into_draft');
					queuePendingCommentInsert(action.parentBonusId, formatted);
					endAutodraftSession();
					navigate(`/bonuses/${action.parentBonusId}`);
					closePanelIfSlashOpened();
					return;
				}
				// No parentBonusId on the action — this is a recognition
				// surface or a non-contextual draft. Open the give-box and
				// forward the optional amount / hashtags so a draft card
				// that carried "+10 #teamwork" lands in the modal with the
				// pill prefilled instead of an empty composer the user
				// has to retype. Mirrors the `open_in_givebox` handler;
				// `recipientRef` isn't on the InsertIntoDraftAction
				// schema (the surface that fires this already has a
				// recipient pill mounted from the give-box itself), so
				// we don't pass it.
				trackContextual(AnalyticsEvent.Bizy.ContextualInserted, 'insert_into_draft');
				handOffToGiveBox(action.text, {
					amount: action.amount,
					hashtags: action.hashtags,
				});
				return;
			}

			case 'send_now': {
				// Both surfaces (recognition + comment) post a real user
				// message into the Bizy chat so the conversation reads
				// naturally:
				//   Recognition (top-level):  "Give it now" → Bizy: "Sent +10 to
				//     Judith — done!" (via give_recognition)
				//   Comment add-on (amount>0): "Add the +N now" → Bizy: "Added
				//     +N to Sam's recognition — done!" (via give_recognition with
				//     parent_bonus_id; this is what makes a points-bearing
				//     pile-on actually carry points)
				//   Zero-point comment:        "Send the comment" → Bizy:
				//     "Posted to Sam's thread — done!" (via comment_on_recognition)
				//
				// The `bizy_action: 'commit_*'` context flag re-enables
				// the relevant post tool for this single turn (it's
				// otherwise disabled in drafting mode), so Bizy actually
				// creates the bonus / comment via its audited tool-call
				// path — analytics, audit logs, and tool tracking all
				// flow through the agent layer just like manual chat
				// sends. No modal flash, no auto-submit, no silent
				// composer mutation.
				//
				// HABITS-1294 regression: the old comment branch always
				// routed to comment_on_recognition (a zero-point-only
				// tool that strips +N from the message). A "+5 just
				// agree" draft card would post as a zero-point comment
				// — the user saw +5 on the card and got 0 in the feed.
				// Now we branch on `action.amount` so a points-bearing
				// pile-on routes through give_recognition's add-on path
				// (with parent_bonus_id), preserving the points.
				trackContextual(AnalyticsEvent.Bizy.ContextualInserted, 'send_now');
				if (action.parentBonusId) {
					if (action.amount != null && action.amount > 0) {
						sendChatMessage('Add the points now', {
							bizy_action: 'commit_draft',
							bizy_draft_payload: JSON.stringify({
								parent_bonus_id: action.parentBonusId,
								message: action.text,
								amount: action.amount,
								...(action.hashtags && action.hashtags.length > 0 ? { hashtags: action.hashtags } : {}),
							}),
						});
						// The draft is posted — end the Pollinator session so the
						// next turn isn't still drafting on this post.
						endAutodraftSession();
						return;
					}
					sendChatMessage('Send the comment', {
						bizy_action: 'commit_comment_draft',
						bizy_comment_draft_payload: JSON.stringify({
							parent_bonus_id: action.parentBonusId,
							message: action.text,
						}),
					});
					endAutodraftSession();
					return;
				}
				sendChatMessage('Give it now', {
					bizy_action: 'commit_draft',
					...(action.draft ? { bizy_draft_payload: JSON.stringify(action.draft) } : {}),
				});
				return;
			}

			case 'open_in_givebox': {
				handOffToGiveBox(action.text, {
					recipientRef: action.recipientRef,
					amount: action.amount,
					hashtags: action.hashtags,
					...(action.attachment ? { attachment: buildSocialAttachment(action.attachment) } : {}),
				});
				// Record the edit-in-givebox intent (fire-and-forget); the give-box hand-off above is unchanged.
				if (currentConversationId) {
					recordGiveboxEditIntent({
						conversationId: currentConversationId,
						recipientIds: action.recipientRef?.id ? [action.recipientRef.id] : [],
						amount: action.amount,
						hashtags: action.hashtags,
						message: action.text,
					});
				}
				return;
			}

			case 'attach_gif': {
				// Gif selection (or removal) on a recognition draft. Posts a
				// deterministic chat turn (mirrors commit_draft); on select, show
				// the picked gif as the user-turn thumbnail, on remove plain text.
				const picked = action.payload.attachment as { gif?: string; mp4?: string } | null | undefined;
				const previewUrl = picked?.gif || picked?.mp4;
				sendChatMessage(
					previewUrl ? 'Use this gif' : 'Remove the gif',
					{ bizy_action: 'attach_gif', bizy_draft_payload: JSON.stringify(action.payload) },
					previewUrl ? { attachmentPreviewUrl: previewUrl } : undefined
				);
				return;
			}

			case 'draft_doodle': {
				// "Add a doodle" CTA on a recognition draft. Posts a deterministic
				// chat turn (mirrors attach_gif/commit_draft) that asks the backend to
				// generate doodle strokes; the backend replies with a standalone
				// `doodle_card` node (the preview, edit, and "Add to draft" live there).
				sendChatMessage('Draw me a doodle', { bizy_action: 'draft_doodle', bizy_draft_payload: JSON.stringify(action.payload) });
				return;
			}

			case 'attach_doodle': {
				// The user finished editing the doodle and it's been uploaded to a URL.
				// Mirrors attach_gif: post a deterministic turn carrying the draft +
				// the doodle image attachment, showing the doodle as the user-turn
				// thumbnail.
				const previewUrl = action.payload.attachment.gif;
				sendChatMessage(
					'Use this doodle',
					{ bizy_action: 'attach_doodle', bizy_draft_payload: JSON.stringify(action.payload) },
					previewUrl ? { attachmentPreviewUrl: previewUrl } : undefined
				);
				return;
			}

			case 'view_post': {
				navigate(action.url);
				return;
			}

			case 'dismiss': {
				if (currentSession()) clearPendingContextualInsert();
				closePanel('ui_block_action');
				return;
			}

			case 'commit_user_action': {
				// User-admin draft/confirm card (INS-1011). Clicking Confirm
				// IS the admin's approval — post a fresh chat turn carrying
				// the verbatim `{action, params}` JSON string so the
				// UserAdmin agent's commit-mode prompt replays the tool with
				// `confirmed=true`. We forward `commitPayload` untouched
				// (it's already a JSON string from the backend); parsing or
				// re-serializing here would risk re-camelizing the
				// snake_case tool-input keys it deliberately preserves.
				sendChatMessage(action.text, {
					bizy_action: 'commit_user_action',
					bizy_user_draft_payload: action.commitPayload,
				});
				return;
			}

			case 'cancel_user_action': {
				// Discard the pending user-admin action and let Bizy
				// acknowledge the cancellation in chat. No tool runs — the
				// agent's default prompt handles `cancel_user_action`.
				sendChatMessage(action.text, {
					bizy_action: 'cancel_user_action',
				});
				return;
			}

			case 'request_draft': {
				// Pollinator hand-off: don't post anything directly
				// — enter a contextual COMMENT drafting session anchored to the
				// target post, then let the panel auto-send the friendly,
				// ID-free starter. Routing through the comment surface makes
				// Bizy draft an editable reply ON the existing post
				// (generate_comment_draft → comment_draft_card with
				// parentBonusId — an add-on when the message states points,
				// else a zero-point comment), never a new give-box recognition.
				//
				// Using the persistent store (not a one-off sendChatMessage
				// context) is what keeps the surface attached to EVERY
				// follow-up turn ("make it 10 points", "warmer"), so the draft
				// never reverts to the give-box mid-conversation. `autodraft`
				// tells the conductor to skip the clarifying-question interview
				// since the user already chose to draft by clicking the button.
				//
				// Idempotent: a duplicate click on the SAME button for the SAME
				// post must not re-enter the session and re-fire the starter
				// (which would enqueue a duplicate user turn + parallel draft).
				// We compare BOTH the starter message AND the parent post, since
				// two different cards can share a friendly prompt (same receiver
				// name) — switching to a different post (or button) still re-drafts.
				const active = currentSession();
				if (active?.surfaceData?.autodraft && active.starterMessage === action.message && active.surfaceData.parentBonusId === action.parentBonusId) {
					return;
				}

				// Measures Pollinator hand-off adoption — reuses the contextual-invoked
				// event (this DOES open a contextual comment drafting session), with a
				// source that distinguishes it from the `/` composer entry.
				track(AnalyticsEvent.Bizy.ContextualInvoked, {
					surface: 'comment',
					source: 'appreciation_card',
					...(action.intent ? { intent: action.intent } : {}),
					path: window.location.pathname,
				});
				setPendingContext({
					surface: 'comment',
					surfaceData: {
						mode: 'drafting',
						autodraft: true,
						// The post's addressing facts (receiver/author names, reason,
						// the user's role) are resolved server-side from
						// `parentBonusId` on the drafting turn, so the only thing the
						// card hands back is the post id.
						...(action.parentBonusId ? { parentBonusId: action.parentBonusId } : {}),
					},
					starterMessage: action.message,
					// The appreciation card always lives inside an already-open
					// panel, so completion actions keep the panel open.
					panelWasAlreadyOpen: true,
				});
				return;
			}
		}
	};
}

/**
 * React consumer hook for {@link buildBizyUiBlockActionDispatcher}.
 * Used by the chat view to build the dispatcher and pass it to the
 * shared `DocumentRenderer` via its `dispatchUiBlockAction` prop.
 */
export function useBuildBizyUiBlockActionDispatcher(
	deps: Omit<BuildDispatcherDeps, 'dispatch' | 'track' | 'recordGiveboxEditIntent'>
): BizyUiBlockActionDispatcher {
	const dispatch = useDispatch();
	const { track } = useAnalytics();
	const recordEditIntent = useRecordGiveboxEditIntentMutation();
	const recordGiveboxEditIntent = useCallback<BuildDispatcherDeps['recordGiveboxEditIntent']>(
		({ conversationId, recipientIds, amount, hashtags, message }) => {
			recordEditIntent.mutate(
				{ conversation_id: conversationId, recipient_ids: recipientIds, amount, hashtags, message },
				// Fire-and-forget: opening the give box already succeeded for the user.
				{ onError: () => undefined }
			);
		},
		[recordEditIntent]
	);
	// Mirror the live `pendingContextualInsert` into a ref so action
	// callbacks inside the memoized dispatcher always read the latest
	// value on click — `useMemo` captures by value, which would let
	// `trackContextual` skip analytics or fire after a session was
	// already cleared if state and click landed in the same render.
	const pendingContextualInsertRef = useRef(deps.pendingContextualInsert);
	pendingContextualInsertRef.current = deps.pendingContextualInsert;

	return useMemo(
		() =>
			buildBizyUiBlockActionDispatcher({
				...deps,
				get pendingContextualInsert() {
					return pendingContextualInsertRef.current;
				},
				dispatch,
				track,
				recordGiveboxEditIntent,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- pendingContextualInsert is read through a ref (above), so it is intentionally excluded; the rest are stable references that should rebuild the dispatcher when they change.
		[
			deps.clearPendingContextualInsert,
			deps.closePanel,
			deps.navigate,
			deps.currentConversationId,
			deps.getCommentComposer,
			deps.queuePendingCommentInsert,
			deps.sendChatMessage,
			deps.setPendingContext,
			dispatch,
			track,
			recordGiveboxEditIntent,
		]
	);
}

/**
 * Build the text we hand to `composer.insertDraft`. The composer's
 * `ActionInputHandle.insertWithHighlight` writes whatever string we
 * give it into the contenteditable, and the editor's
 * BonusReasonParser then derives `data().amount` from a leading `+N`
 * token. So to land an add-on amount on a Bizy-drafted comment we
 * have to bake the `+N` into the inserted text itself — the composer
 * exposes no separate "set amount" API.
 *
 * Hashtags are deliberately NOT prepended/appended here. Comments
 * rarely carry hashtags (the agent prompt biases against them), and
 * when a comment does opt in for hashtags they belong on the
 * `give_recognition` add-on path (Send-now branches on amount), not
 * on a free-form text insert that the user is about to edit.
 */
function formatCommentDraftText(text: string, amount?: number): string {
	if (amount == null || amount <= 0) return text;
	return `+${amount} ${text}`;
}

/**
 * Constructs a `Recipient` (the give-box's @ pill data class) from
 * the typed payload Bizy ships on `open_in_givebox` / `send_now`
 * actions. Mirrors the snake_case shape `Recipient`'s constructor
 * expects so the modal's `createRecognitionContent` can render the
 * pill with avatar + display name on first paint — no follow-up
 * fetch, no flash of empty `@`.
 */
function buildRecipient(ref: BizyUiBlockRecipientRef): Recipient {
	return new Recipient({
		id: ref.id,
		username: ref.username,
		display_name: ref.displayName,
		profile_pic_url: ref.profilePicUrl ?? '',
		user_mode: 'normal',
	});
}

/**
 * Builds a `SocialAttachment` (the give-box's gif data class) from the gif a
 * user picked in chat. The wire shape is camelCase (`altText`); the entity
 * constructor takes snake_case (`alt_text`), so we map it here.
 */
function buildSocialAttachment(attachment: GifAttachment): SocialAttachment {
	return new SocialAttachment({
		category: AttachmentCategory.Social,
		gif: attachment.gif ?? '',
		mp4: attachment.mp4 ?? '',
		alt_text: attachment.altText ?? undefined,
	});
}
